/**
 * PlatformVoiceEditor — per-author per-platform voice editor.
 *
 * Renders the canonical PlatformVoice schema fields 1:1 (no wrapper types,
 * no csv-encoding). Schema source: openapi-content-plan.yaml > PlatformVoice
 * (1.3.1+). All field names and types match the AI handler's structured-output
 * schema and what AuthorOnboardingHandler.persistOnboardingBundle writes to
 *   projects/{pid}/authors/{authorId}/platforms/{platformId}/voice/voice
 *
 * Rendered fields (strict canonical):
 *   • voice               (string — one-liner trait)
 *   • toneVariations      (Record<postType, string> — editable rows + add)
 *   • vocabulary.use      (string[] — multiline newline-split)
 *   • vocabulary.avoid    (string[] — multiline newline-split)
 *   • avoidPatterns       (string[] — multiline newline-split)
 *   • structurePreferences(string[] — multiline newline-split)
 *   • emojiPolicy         (string — Select with presets + free-form fallback)
 *   • hashtagPolicy       (string — multiline free-form)
 *   • platformNotes       (string — multiline)
 *   • source              (read-only chip)
 *   • sourcePostIds       (read-only count caption)
 *   • lastExtractedAt     (read-only timestamp caption)
 *   • isComplete          (Switch "Mark voice done")
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { usePlatformVoice } from '../../hooks/usePlatformVoice';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { ConfirmActionDialog, type ConfirmActionResult } from '../author/ConfirmActionDialog';
import { useProjectDefaultModel } from '../../hooks/useProjectDefaultModel';
import type { PlatformVoice } from '../../types';
import type { AuthorBasePath } from '../../services/AuthorPlatformService';

const TESTID = 'platform-voice-editor';

// ─── Conversion helpers (string[] ↔ multiline string) ──────────────────

const linesToText = (lines?: string[]): string => (lines ?? []).join('\n');

const textToLines = (text: string): string[] =>
  text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

// ─── Known emoji policy presets — UI hint only, schema is free-form ────

const EMOJI_POLICY_PRESETS = ['none', 'minimal', 'active'] as const;
const KNOWN_TONE_KEYS = ['education', 'promotion', 'case_study', 'announcement', 'personal'];

// ─── Source enum → human label + chip color ────────────────────────────

const SOURCE_LABEL: Record<string, { label: string; color: 'success' | 'default' | 'info' | 'warning' }> = {
  ai_drafted: { label: 'AI drafted', color: 'info' },
  manual: { label: 'Manual', color: 'default' },
  copied_from: { label: 'Copied from another platform', color: 'default' },
  mixed: { label: 'AI + edited', color: 'warning' },
};

export interface PlatformVoiceEditorProps {
  projectId: string;
  authorId: string;
  platformId: string;
  /** When user clicks Re-extract, caller is informed so a task tray can subscribe. */
  userId: string;
  /** Discriminates self-author vs tracked creator. Default `'authors'`. */
  basePath?: AuthorBasePath;
  onTaskStarted?: (taskId: string, label: string) => void;
}

interface DraftState {
  voice: string;
  toneVariations: Record<string, string>;
  vocabularyUse: string[];
  vocabularyAvoid: string[];
  avoidPatterns: string[];
  structurePreferences: string[];
  emojiPolicy: string;
  hashtagPolicy: string;
  platformNotes: string;
  isComplete: boolean;
}

const EMPTY_DRAFT: DraftState = {
  voice: '',
  toneVariations: {},
  vocabularyUse: [],
  vocabularyAvoid: [],
  avoidPatterns: [],
  structurePreferences: [],
  emojiPolicy: '',
  hashtagPolicy: '',
  platformNotes: '',
  isComplete: false,
};

function voiceToDraft(v: PlatformVoice | null): DraftState {
  if (!v) return EMPTY_DRAFT;
  return {
    voice: v.voice ?? '',
    toneVariations: { ...(v.toneVariations ?? {}) },
    vocabularyUse: [...(v.vocabulary?.use ?? [])],
    vocabularyAvoid: [...(v.vocabulary?.avoid ?? [])],
    avoidPatterns: [...(v.avoidPatterns ?? [])],
    structurePreferences: [...(v.structurePreferences ?? [])],
    emojiPolicy: v.emojiPolicy ?? '',
    hashtagPolicy: v.hashtagPolicy ?? '',
    platformNotes: v.platformNotes ?? '',
    isComplete: Boolean(v.isComplete),
  };
}

export const PlatformVoiceEditor: React.FC<PlatformVoiceEditorProps> = ({
  projectId,
  authorId,
  platformId,
  userId,
  basePath = 'authors',
  onTaskStarted,
}) => {
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const { voice, dirtyFields, loading, save } = usePlatformVoice(
    projectId,
    authorId,
    platformId,
    basePath,
  );

  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [newToneKey, setNewToneKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [reextractDialogOpen, setReextractDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync draft when voice doc loads/updates (and isn't being actively edited)
  useEffect(() => {
    setDraft(voiceToDraft(voice));
  }, [voice]);

  // ─── toneVariations editing helpers ──────────────────────────────────

  const setTone = (key: string, value: string) => {
    setDraft((d) => ({ ...d, toneVariations: { ...d.toneVariations, [key]: value } }));
  };

  const removeTone = (key: string) => {
    setDraft((d) => {
      const next = { ...d.toneVariations };
      delete next[key];
      return { ...d, toneVariations: next };
    });
  };

  const addTone = () => {
    const k = newToneKey.trim();
    if (!k) return;
    if (draft.toneVariations[k] !== undefined) return; // already exists
    setTone(k, '');
    setNewToneKey('');
  };

  // ─── Save / Re-extract ───────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Detect whether this is post-AI manual edit → flip source to 'mixed'.
      const isAiDrafted = voice?.source === 'ai_drafted';
      const userEdited = dirtyFields.size > 0 || draft.voice !== (voice?.voice ?? '');
      const nextSource: PlatformVoice['source'] =
        isAiDrafted && userEdited ? 'mixed' : voice?.source ?? 'manual';

      await save({
        voice: draft.voice || undefined,
        toneVariations:
          Object.keys(draft.toneVariations).length > 0 ? draft.toneVariations : undefined,
        vocabulary:
          draft.vocabularyUse.length > 0 || draft.vocabularyAvoid.length > 0
            ? { use: draft.vocabularyUse, avoid: draft.vocabularyAvoid }
            : undefined,
        avoidPatterns: draft.avoidPatterns.length > 0 ? draft.avoidPatterns : undefined,
        structurePreferences:
          draft.structurePreferences.length > 0 ? draft.structurePreferences : undefined,
        emojiPolicy: draft.emojiPolicy || undefined,
        hashtagPolicy: draft.hashtagPolicy || undefined,
        platformNotes: draft.platformNotes || undefined,
        source: nextSource,
        isComplete: draft.isComplete,
      });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save voice');
    } finally {
      setSaving(false);
    }
  };

  const handleReextractConfirm = async (result: ConfirmActionResult) => {
    setReextracting(true);
    setError(null);
    try {
      const client = new ContentPlanApiClient();
      const res = await client.authorAnalyze({
        projectId,
        userId,
        existingAuthorId: authorId,
        platformId,
        ...({
          scope: 'voice',
          model: result.model,
          analysisInstructions: result.analysisInstructions,
          ...(basePath === 'external_authors'
            ? { authorKind: 'tracked-creator' }
            : {}),
        } as any),
      });
      if (!res.success || !res.taskId) {
        throw new Error(res.error ?? 'Re-extract dispatch failed');
      }
      onTaskStarted?.(res.taskId, `Re-extract ${platformId} voice`);
    } catch (e: any) {
      setError(e?.message ?? 'Re-extract failed');
      throw e;
    } finally {
      setReextracting(false);
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }} data-testid={TESTID}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  const sourceMeta = voice?.source ? SOURCE_LABEL[voice.source] : null;
  const lastExtractCaption = voice?.lastExtractedAt
    ? `Last AI extract: ${new Date(voice.lastExtractedAt).toLocaleString()}`
    : null;
  const sourcePostsCaption =
    voice?.sourcePostIds && voice.sourcePostIds.length > 0
      ? `Voice extracted from ${voice.sourcePostIds.length} post${
          voice.sourcePostIds.length === 1 ? '' : 's'
        }.`
      : null;

  return (
    <Paper variant="outlined" sx={{ p: 3 }} data-testid={TESTID}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Voice — {platformId}</Typography>
          <Typography variant="caption" color="text.secondary">
            Per-platform tone, vocabulary, structure preferences. Auto-extracted
            during onboarding; edit freely.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {sourceMeta && (
            <Chip
              size="small"
              color={sourceMeta.color}
              variant="outlined"
              label={sourceMeta.label}
              data-testid={`${TESTID}-source-chip`}
            />
          )}
          {dirtyFields.size > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`${dirtyFields.size} edited locally`}
              data-testid={`${TESTID}-dirty-count`}
            />
          )}
          <Tooltip title="Re-extract voice from corpus (analyze-only — opens dialog with optional analysis focus)">
            <span>
              <IconButton
                size="small"
                onClick={() => setReextractDialogOpen(true)}
                disabled={reextracting}
                data-testid="platform-voice-reextract-btn"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {(lastExtractCaption || sourcePostsCaption) && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          {sourcePostsCaption && (
            <Typography variant="caption" color="text.secondary">
              {sourcePostsCaption}
            </Typography>
          )}
          {lastExtractCaption && (
            <Typography variant="caption" color="text.secondary">
              {lastExtractCaption}
            </Typography>
          )}
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {/* voice — single one-liner trait */}
        <TextField
          label="Voice"
          size="small"
          fullWidth
          value={draft.voice}
          onChange={(e) => setDraft({ ...draft, voice: e.target.value })}
          helperText="One characterising trait — e.g. 'expert-sceptic', 'hot-take-grounded', 'friendly-analytical'"
          inputProps={{ 'data-testid': 'platform-voice-input' }}
          color={dirtyFields.has('voice') ? 'warning' : 'primary'}
        />

        {/* toneVariations — editable rows + add new */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Tone variations
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            How tone shifts per post type (education, promotion, case_study, …). Free-form keys allowed.
          </Typography>
          <Stack spacing={1}>
            {Object.keys(draft.toneVariations).length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No tone variations yet. AI usually drafts {KNOWN_TONE_KEYS.join(', ')} during onboarding.
              </Typography>
            )}
            {Object.entries(draft.toneVariations).map(([key, value]) => (
              <Stack key={key} direction="row" spacing={1} alignItems="flex-start">
                <Chip
                  label={key}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 110, justifyContent: 'flex-start' }}
                />
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  value={value}
                  onChange={(e) => setTone(key, e.target.value)}
                  inputProps={{ 'data-testid': `platform-tone-${key}-input` }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeTone(key)}
                  data-testid={`platform-tone-${key}-remove-btn`}
                  title="Remove tone variation"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="postType key (e.g. announcement)"
                value={newToneKey}
                onChange={(e) => setNewToneKey(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTone();
                  }
                }}
                inputProps={{ 'data-testid': 'platform-tone-new-key-input' }}
              />
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addTone}
                disabled={!newToneKey.trim()}
                data-testid="platform-tone-add-btn"
              >
                Add
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* vocabulary — two side-by-side multiline arrays */}
        <Stack direction="row" spacing={2}>
          <TextField
            label="Vocabulary — use"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={linesToText(draft.vocabularyUse)}
            onChange={(e) =>
              setDraft({ ...draft, vocabularyUse: textToLines(e.target.value) })
            }
            helperText="Preferred words/phrases — one per line"
            inputProps={{ 'data-testid': 'platform-vocab-use-input' }}
            color={dirtyFields.has('vocabulary') ? 'warning' : 'primary'}
          />
          <TextField
            label="Vocabulary — avoid"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={linesToText(draft.vocabularyAvoid)}
            onChange={(e) =>
              setDraft({ ...draft, vocabularyAvoid: textToLines(e.target.value) })
            }
            helperText="Forbidden words/phrases — one per line"
            inputProps={{ 'data-testid': 'platform-vocab-avoid-input' }}
            color={dirtyFields.has('vocabulary') ? 'warning' : 'primary'}
          />
        </Stack>

        {/* avoidPatterns — multiline array */}
        <TextField
          label="Avoid patterns"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={linesToText(draft.avoidPatterns)}
          onChange={(e) =>
            setDraft({ ...draft, avoidPatterns: textToLines(e.target.value) })
          }
          helperText="Structural patterns to forbid (e.g. 'Most companies do X wrong'). One per line."
          inputProps={{ 'data-testid': 'platform-avoid-patterns-input' }}
          color={dirtyFields.has('avoidPatterns') ? 'warning' : 'primary'}
        />

        {/* structurePreferences — multiline array (was string in old code — BUG) */}
        <TextField
          label="Structure preferences"
          size="small"
          fullWidth
          multiline
          minRows={3}
          value={linesToText(draft.structurePreferences)}
          onChange={(e) =>
            setDraft({ ...draft, structurePreferences: textToLines(e.target.value) })
          }
          helperText={`Post structure templates — one per line. Example:
"Hook → My failure → What I'd do now → Question"`}
          inputProps={{ 'data-testid': 'platform-structure-input' }}
          color={dirtyFields.has('structurePreferences') ? 'warning' : 'primary'}
        />

        {/* emojiPolicy — free-form string with preset suggestions */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Emoji policy
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, mt: 0.5 }}>
            <Select
              size="small"
              value={EMOJI_POLICY_PRESETS.includes(draft.emojiPolicy as any) ? draft.emojiPolicy : ''}
              onChange={(e) => setDraft({ ...draft, emojiPolicy: e.target.value })}
              displayEmpty
              sx={{ minWidth: 140 }}
              data-testid="platform-emoji-policy-select"
            >
              <MenuItem value="">
                <em>Custom…</em>
              </MenuItem>
              {EMOJI_POLICY_PRESETS.map((preset) => (
                <MenuItem key={preset} value={preset}>
                  {preset}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary">
              or describe a custom rule below
            </Typography>
          </Stack>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={1}
            value={draft.emojiPolicy}
            onChange={(e) => setDraft({ ...draft, emojiPolicy: e.target.value })}
            placeholder="e.g. 'functional arrows only, no faces' or pick a preset above"
            inputProps={{ 'data-testid': 'platform-emoji-policy-input' }}
            color={dirtyFields.has('emojiPolicy') ? 'warning' : 'primary'}
          />
        </Box>

        {/* hashtagPolicy — free-form multiline */}
        <TextField
          label="Hashtag policy"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={draft.hashtagPolicy}
          onChange={(e) => setDraft({ ...draft, hashtagPolicy: e.target.value })}
          helperText="Free-form rules — count, placement, topical focus. e.g. '3-5 at end, topical only, no generic #AI'"
          inputProps={{ 'data-testid': 'platform-hashtag-policy-input' }}
          color={dirtyFields.has('hashtagPolicy') ? 'warning' : 'primary'}
        />

        {/* platformNotes — free-form multiline */}
        <TextField
          label="Platform notes"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={draft.platformNotes}
          onChange={(e) => setDraft({ ...draft, platformNotes: e.target.value })}
          helperText="Platform-specific nuances beyond the structured fields. Feeds DerivativePostHandler."
          inputProps={{ 'data-testid': 'platform-notes-input' }}
          color={dirtyFields.has('platformNotes') ? 'warning' : 'primary'}
        />

        {/* isComplete — Switch */}
        <FormControlLabel
          control={
            <Switch
              checked={draft.isComplete}
              onChange={(e) => setDraft({ ...draft, isComplete: e.target.checked })}
              data-testid="platform-voice-complete-switch"
            />
          }
          label={
            <Typography variant="body2">
              Mark voice done {draft.isComplete && '(reviewed and confirmed)'}
            </Typography>
          }
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving}
            onClick={handleSave}
            data-testid="platform-voice-save-btn"
          >
            {saving ? 'Saving…' : 'Save voice'}
          </Button>
        </Stack>
      </Stack>

      <ConfirmActionDialog
        open={reextractDialogOpen}
        projectId={projectId}
        title={`Re-extract ${platformId} voice`}
        description="AI re-derives PlatformVoice from the existing corpus (excluding any posts you've deleted). Author identity, posts list, and topic pillars are NOT touched."
        mode="ai"
        defaultModel={projectDefaultModel.value}
        confirmLabel="Re-extract"
        onConfirm={handleReextractConfirm}
        onClose={() => setReextractDialogOpen(false)}
      />
    </Paper>
  );
};

export default PlatformVoiceEditor;
